import type { CalendarSessionInput, CoachChatResponse } from '@/lib/api';

/**
 * Extracts calendar sessions from a CoachChatResponse.
 * Prefers season_plan.calendar_sessions, falls back to planned_weeks, then week_plan.sessions.
 */
export function extractCalendarSessions(response: CoachChatResponse): CalendarSessionInput[] {
  const calSessions = response.season_plan?.calendar_sessions;
  if (calSessions && calSessions.length > 0) {
    return calSessions.map((s) => ({
      date: s.date,
      workout_type: s.workout_type,
      duration_or_structure: s.duration_or_structure ?? '',
      notes: s.notes ?? '',
    }));
  }

  if (response.planned_weeks && response.planned_weeks.length > 0) {
    const sessions: CalendarSessionInput[] = [];
    for (const week of response.planned_weeks) {
      for (const sess of week.sessions) {
        sessions.push({
          date: sess.date,
          workout_type: sess.type,
          duration_or_structure: `${sess.duration ?? 0} min`,
          notes: sess.notes ?? '',
        });
      }
    }
    return sessions;
  }

  // Third fallback: week_plan.sessions (returned by coach_chat week plan executor)
  const weekPlan = (response as Record<string, unknown>).week_plan as
    | { sessions?: Array<Record<string, unknown>> }
    | undefined;
  if (weekPlan && Array.isArray(weekPlan.sessions) && weekPlan.sessions.length > 0) {
    return weekPlan.sessions.map((sess) => {
      const rawDate = (sess.date as string) ?? '';
      const dateStr = typeof rawDate === 'string'
        ? rawDate.substring(0, 10)
        : String(rawDate).substring(0, 10);
      const durationMin = (sess.duration_minutes as number) ?? 0;
      const distanceKm = (sess.distance_km as number) ?? 0;
      const durationOrStructure = durationMin
        ? `${Math.round(durationMin)} min`
        : distanceKm
          ? `${(distanceKm / 1.60934).toFixed(1)} mi`
          : '';
      return {
        date: dateStr,
        workout_type: ((sess.intensity as string) ?? (sess.type as string) ?? 'easy'),
        duration_or_structure: durationOrStructure,
        notes: ((sess.notes as string) ?? (sess.title as string) ?? ''),
      } satisfies CalendarSessionInput;
    });
  }

  return [];
}

/**
 * Determines whether a CoachChatResponse contains a loadable plan.
 * Use this to conditionally render the button.
 */
export function hasLoadablePlan(response: CoachChatResponse): boolean {
  return extractCalendarSessions(response).length > 0;
}
